import useSWR from 'swr';
import { getRelationHistoryUrl } from '@/constant/approval';
import {
  RelationHistoryAction,
  RelationHistoryBatch,
  RelationHistoryItem,
} from '@/types/approval';
import { getNestedDataFetcher } from '@/utils/apis';

/**
 * 연관품의 변경이력 fetch + 배치 그룹핑 (updatedAt desc → `groupHistoryByBatch`).
 * - documentId / shouldFetch 둘 다 truthy 일 때만 호출 (모달 열린 동안만).
 * - 응답 shape 가 백엔드 확정 전이라 lenient 가드 (배열 아니면 빈 배열 폴백).
 */

const EMPTY_BATCHES: RelationHistoryBatch[] = [];
const EMPTY_ITEMS: RelationHistoryItem[] = [];

// 동일 배치 판정 임계값. PHP 의 1초 미만 (= 1000 미만 ms).
const BATCH_THRESHOLD_MS = 1000;

/**
 * ISO 'YYYY-MM-DDTHH:mm:ss[.SSS][Z]' / 'YYYY-MM-DD HH:mm:ss' 둘 다 허용.
 * 파싱 실패 시 NaN.
 */
const parseTime = (s?: string): number => {
  if (!s) return Number.NaN;
  // 'YYYY-MM-DD HH:mm:ss' 케이스에 대비해 공백을 T 로 치환 (ISO 호환).
  const normalized = s.includes('T') ? s : s.replace(' ', 'T');
  const t = new Date(normalized).getTime();
  return Number.isFinite(t) ? t : Number.NaN;
};

/**
 * desc 정렬된 항목들을 배치로 그룹핑.
 * 같은 배치 조건: 인접 항목과 updatedAt 차이 < 1초 AND 같은 action AND 같은 actor.id
 * (action 이 다르면 표시 문장이 달라 시각적으로 분리). 대표 시간 = 배치 내 가장 이른 updatedAt.
 */
export const groupHistoryByBatch = (
  itemsDesc: RelationHistoryItem[],
): RelationHistoryBatch[] => {
  const batches: RelationHistoryBatch[] = [];

  for (const item of itemsDesc) {
    const lastBatch = batches[batches.length - 1];
    const itemTime = parseTime(item.updatedAt);

    if (
      lastBatch &&
      lastBatch.action === item.action &&
      lastBatch.actor?.id === item.actor?.id &&
      Number.isFinite(itemTime) &&
      Math.abs(parseTime(lastBatch.updatedAt) - itemTime) < BATCH_THRESHOLD_MS
    ) {
      lastBatch.items.push(item);
      // 대표 시간은 배치 내 가장 이른 updatedAt 로 유지.
      const lastTime = parseTime(lastBatch.updatedAt);
      if (Number.isFinite(itemTime) && itemTime < lastTime) {
        lastBatch.updatedAt = item.updatedAt ?? lastBatch.updatedAt;
      }
      continue;
    }

    batches.push({
      id:
        item.id ??
        `${item.action}-${item.actor?.id ?? 'anon'}-${
          item.updatedAt ?? batches.length
        }`,
      action: item.action,
      actor: item.actor,
      updatedAt: item.updatedAt ?? '',
      items: [item],
    });
  }

  return batches;
};

/**
 * 응답 데이터 정규화 — 배열이 아닌 경우 빈 배열로 폴백.
 * action 누락 항목은 'create' 로 보수적 폴백.
 */
const normalizeItems = (raw: unknown): RelationHistoryItem[] => {
  if (!Array.isArray(raw)) return EMPTY_ITEMS;
  return raw
    .filter(
      (row): row is Record<string, unknown> => !!row && typeof row === 'object',
    )
    .map((row) => {
      const action = (row.action as RelationHistoryAction) ?? 'create';
      return {
        ...(row as RelationHistoryItem),
        action,
      } as RelationHistoryItem;
    });
};

const useRelationHistory = (
  documentId?: string | null,
  shouldFetch?: boolean,
) => {
  const { data, error, isLoading, mutate } = useSWR<RelationHistoryItem[]>(
    documentId && shouldFetch ? getRelationHistoryUrl(documentId) : null,
    getNestedDataFetcher,
  );

  const items = normalizeItems(data);
  // updatedAt desc 정렬 (사용자 명세). 시간 누락 항목은 뒤로.
  const sorted = [...items].sort((a, b) => {
    const at = parseTime(a.updatedAt);
    const bt = parseTime(b.updatedAt);
    const aOk = Number.isFinite(at);
    const bOk = Number.isFinite(bt);
    if (!aOk && !bOk) return 0;
    if (!aOk) return 1;
    if (!bOk) return -1;
    return bt - at;
  });

  const batches = sorted.length ? groupHistoryByBatch(sorted) : EMPTY_BATCHES;

  return {
    batches,
    isLoading,
    error,
    mutate,
  };
};

export default useRelationHistory;
